/**
 * FlowCanvas.tsx
 * React Flow canvas wrapper with standard controls.
 */
import type { ReactNode, RefObject } from 'react';
import {
  ReactFlow,
  Background,
  type Connection,
  type EdgeMouseHandler,
  type NodeMouseHandler,
  type NodeTypes,
  type OnNodeDrag,
  type OnEdgesChange,
  type OnNodesChange,
} from '@xyflow/react';

import type { AppEdge, AppNode } from '../types';
import type { FlowLayoutPreset } from '../config/layoutPreset';

const EDGE_CURVATURE = 0.75;
const DEFAULT_EDGE_OPTIONS = {
  type: 'default' as const,
  pathOptions: { curvature: EDGE_CURVATURE },
};
const INITIAL_FIT_VIEW_OPTIONS = { padding: 0.2 } as const;

type FlowCanvasProps = {
  nodes: AppNode[];
  edges: AppEdge[];
  nodeTypes: NodeTypes;
  onNodesChange: OnNodesChange<AppNode>;
  onEdgesChange: OnEdgesChange<AppEdge>;
  onConnect: (connection: Connection) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  onNodeClick: NodeMouseHandler<AppNode>;
  onNodeDoubleClick: NodeMouseHandler<AppNode>;
  onNodeDragStart?: OnNodeDrag<AppNode>;
  onNodeDragStop?: OnNodeDrag<AppNode>;
  onPaneClick: (event: React.MouseEvent) => void;
  onEdgeContextMenu: EdgeMouseHandler<AppEdge>;
  onNodeContextMenu: NodeMouseHandler<AppNode>;
  viewportPreset: FlowLayoutPreset;
  connectMode?: boolean;
  children?: ReactNode;
  touchMode?: boolean;
  containerRef?: RefObject<HTMLDivElement | null>;
};

export function FlowCanvas({
  nodes,
  edges,
  nodeTypes,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onDragOver,
  onDrop,
  onNodeClick,
  onNodeDoubleClick,
  onNodeDragStart,
  onNodeDragStop,
  onPaneClick,
  onEdgeContextMenu,
  onNodeContextMenu,
  viewportPreset,
  connectMode = false,
  children,
  touchMode = false,
  containerRef,
}: FlowCanvasProps) {
  const isFixedNodesOnly = nodes.length === 2 && (
    nodes.some((node) => node.id === 'start') && nodes.some((node) => node.id === 'end')
    || nodes.some((node) => node.id === 'input') && nodes.some((node) => node.id === 'output')
  );

  return (
    <div ref={containerRef} className='flex-1 relative'>
      <ReactFlow<AppNode, AppEdge>
        nodes={nodes}
        edges={edges}
        defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodeDragStart={onNodeDragStart}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={onPaneClick}
        onEdgeContextMenu={onEdgeContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        connectOnClick={connectMode}
        nodesDraggable={!connectMode}
        panOnDrag={!connectMode}
        fitView
        fitViewOptions={isFixedNodesOnly ? INITIAL_FIT_VIEW_OPTIONS : undefined}
        className={`bg-background flow-strong-connectors flow-size-${viewportPreset} ${touchMode ? 'flow-touch' : ''} ${connectMode ? 'flow-connect-mode' : ''}`}
      >
        <Background color='#c9d5e6' bgColor='#ffffff' size={2.5} />
      </ReactFlow>
      {children}
    </div>
  );
}
